import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Typography, Button, Grid, Box, Paper, CircularProgress, styled } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';

import { useErrorHandler } from '../../utils/useErrorHandle';
import MultipleChoiceQuestion from '../../components/MultipleChoice';
import { autosaveAnswer, fetchExamDetails, fetchExamTime, moveNextModule, skipBreak } from '../../actions/auth';
import FocusLock from 'react-focus-lock';
import BreakPage from '../../components/BreakPage';
import ReviewPageComponent from '../../components/ReviewPage';
import './testpages.css'
import debounce from 'lodash/debounce';
import DOMPurify from 'dompurify';
import { Snackbar } from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import Footer from './Footer';
import CalculatorComponent from '../../components/CalculatorComponent';
import TopHeader from './TopHeader';
import LoadingOverlay from '../../components/LoadingOverlay';
//import the css
import '../css/app.css'
import '../css/bootstrap45.min.css'
import '../css/simplebar.css'

// import Countdown1 from '../../components/Countdown';
const POLLING_INTERVAL = 30000; // 每 30 秒轮询一次 (可以调整)

const TestPage = () => {
    const dispatch = useDispatch();
    const { studentexamId } = useParams();
    const navigate = useNavigate();

    // --- State Hooks ---
    const [currentExam, setCurrentExam] = useState(null);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    // const [error, setError] = useState(''); // Replaced by useErrorHandler
    const [selectedAnswers, setSelectedAnswers] = useState({});
    const [markedForReview, setMarkedForReview] = useState({});
    const [crossedOut, setCrossedOut] = useState({});
    const [saveStatuses, setSaveStatuses] = useState({}); // e.g., { q1: 'synced', q2: 'unsynced', q3: 'syncing', q4: 'error' }
    const [showReviewPage, setShowReviewPage] = useState(false);
    const [endTime, setEndTime] = useState(0); // Module end time (from server)
    const [breakendTime, setBreakendTime] = useState(0); // Break end time (from server)
    const [onBreak, setOnBreak] = useState(false);
    const [calculatorVisible, setCalculatorVisible] = useState(false);
    const [referenceVisible, setReferenceVisible] = useState(false);
    const [calculatorPosition, setCalculatorPosition] = useState({ x: 0, y: 0 });
    const [referencePosition, setReferencePosition] = useState({ x: 0, y: 0 });
    const [isCalculatorExpanded, setIsCalculatorExpanded] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false); // For general info snackbars
    const [snackbarMessage, setSnackbarMessage] = useState('Transitioning to next section...'); // <-- New state for message
    const [isTransitioning, setIsTransitioning] = useState(false); // <-- New state to track transition phase

    const [isMinimapVisible, setIsMinimapVisible] = useState(false);
    const [userInput, setUserInput] = useState(''); // For fill-in-the-blank questions
    const [inputError, setInputError] = useState('');
    const [isPageVisible, setIsPageVisible] = useState(!document.hidden); // Track page visibility
    const [crossedIcon, setCrossedIcon] = useState(false); // State for crossed-out icon toggle
    // const TRANSITION_DELAY = 1000; // 1 second artificial delay (adjust as needed)

    // --- Refs ---
    const isSyncing = useRef(false); // Prevent concurrent sync calls
    const minimapRef = useRef(null);
    const myCalculatorRef = useRef(null);
    const currentQuestionIdRef = useRef(null); // Ref for current question ID (useful in callbacks)
    const currentAnswerRef = useRef(null); // Ref for current answer (useful in callbacks)
    const transitionIntentionRef = useRef(''); // Ref to store the intended message

    // --- Custom Hooks & Selectors ---
    const { errorState, handleError, clearError } = useErrorHandler();
    const handleErrorRef = useRef(handleError); // Ref to ensure latest handleError is used in callbacks
    const user = useSelector(state => state.auth.user);

    // --- Constants & Helper Functions ---
    const getLocalStorageKey = useCallback((id) => `exam_attempt_${id}`, []);

    const processHtmlContent = (html) => {
        // Basic sanitization and replacement (consider more robust solution if needed)
        const sanitized = DOMPurify.sanitize(html);
        return sanitized.replace(/<p>/gi, '<span>').replace(/<\/p>/gi, '</span>');
    };

    // --- Event Handlers & Callbacks ---

    // Input validation for fill-in-the-blank
    const validateInput = useCallback((value) => {
        if (value === null || value === undefined) return true; // Allow empty/null
        const strValue = String(value); // Ensure it's a string for checks

        if (strValue.split('.').length > 2 || strValue.split('/').length > 2) {
            setInputError("You've entered a decimal point or slash in the wrong position");
            return false;
        }
        if (strValue.endsWith('/') || strValue.startsWith('/') || strValue.endsWith('.') || strValue.endsWith('-')) {
            setInputError("Incomplete numeric input");
            return false;
        }
        // Allow empty string, numbers, negative numbers, decimals, fractions
        if (!/^-?\d*(\.\d+)?$|^-?\d+\/\d+$/.test(strValue) && strValue !== '') {
             // Uncomment and refine regex if specific formats are required
             setInputError("Invalid format. Use numbers, decimals (e.g., 3.14), or fractions (e.g., 1/2).");
             return false;
        }
        setInputError(''); // Clear error if valid
        return true;
    }, []);

    // Save answer locally and attempt sync
    const saveCurrentAnswer = useCallback((questionId, currentAnswer) => {
        if (questionId === null || questionId === undefined) {
            console.warn("Attempted to save answer with invalid questionId.");
            return;
        }
        // Allow saving empty/null/undefined as clearing an answer
        // Validation should happen primarily on blur/submit, not necessarily every save call
        if (!validateInput(currentAnswer)) {
            console.warn('Invalid input detected during save attempt:', currentAnswer);
            // Decide if invalid input should prevent saving 'unsynced' status
            return; // Option: prevent saving invalid input
        }

        const localStorageKey = getLocalStorageKey(studentexamId);
        const saveData = {
            answer: currentAnswer,
            status: 'unsynced',
            timestamp: Date.now()
        };

        try {
            let attemptData = JSON.parse(localStorage.getItem(localStorageKey) || '{}');
            // Only update if the answer actually changed from what's in localStorage
            if (attemptData[questionId]?.answer !== currentAnswer || attemptData[questionId]?.status !== 'unsynced') {
                attemptData[questionId] = saveData;
                localStorage.setItem(localStorageKey, JSON.stringify(attemptData));
                // console.log(`Answer for ${questionId} saved locally with status 'unsynced'.`);
                setSaveStatuses(prev => ({ ...prev, [questionId]: 'unsynced' })); // Update UI feedback

                // Attempt network upload
                dispatch(autosaveAnswer(questionId, currentAnswer, studentexamId))
                    .then(() => {
                        // console.log(`Answer for ${questionId} successfully synced.`);
                        let currentAttemptData = JSON.parse(localStorage.getItem(localStorageKey) || '{}');
                        // Verify timestamp to avoid race conditions if multiple saves happened
                        if (currentAttemptData[questionId] && currentAttemptData[questionId].timestamp === saveData.timestamp) {
                            currentAttemptData[questionId].status = 'synced';
                            localStorage.setItem(localStorageKey, JSON.stringify(currentAttemptData));
                            setSaveStatuses(prev => ({ ...prev, [questionId]: 'synced' })); // Update UI feedback
                        }
                    })
                    .catch((error) => {
                      // 3b. Sync Failure (e.g., Offline)
                      console.error(`Failed to sync answer for ${questionId}:`, error);
                    setSaveStatuses(prev => ({ ...prev, [questionId]: 'error' }));

                    // *** Construct and Show User Message ***
                    let userMessage = '';
                    // Check if browser reports offline status
                    if (!navigator.onLine) {
                        userMessage = `You appear to be offline. Your answer was saved locally but could not be synced. Syncing will resume automatically when you're back online.`;
                    } else {
                        // Sync failed for another reason (server error, etc.)
                        userMessage = `Could not sync your answer, but it was saved locally. Please check connection or try again later.`;
                    }

                    handleErrorRef.current(
                        error,
                        userMessage,
                        'warning' // Use 'warning' severity
                    );
                      // *** END USER NOTIFICATION ***
                  });
            } else {
                //  console.log(`Answer for ${questionId} unchanged or already marked 'unsynced', skipping redundant local save/sync trigger.`);
            }

        } catch (e) {
            console.error("Failed to write to localStorage:", e);
            handleErrorRef.current(e, 'Failed to save progress locally. Please check browser storage.');
        }
      }, [studentexamId, dispatch, getLocalStorageKey, handleErrorRef, validateInput /* Add any other state used inside */]);

    // Debounced save function
    const debouncedSave = useMemo(
        () => debounce((qId, value) => {
            saveCurrentAnswer(qId, value);
        }, 1500), // Adjust debounce time as needed (e.g., 1500ms)
        [saveCurrentAnswer] // saveCurrentAnswer is stable due to useCallback
    );

    // Handle answer selection/input changes
// Handle answer selection/input changes - *** CORRECTED VERSION ***
const handleAnswerSelect = (questionId, event) => { // Always expect the event object
  const currentQuestion = currentExam?.current_module?.questions.find(q => q.id === questionId);
  if (!currentQuestion) {
      console.warn(`handleAnswerSelect called for unknown questionId: ${questionId}`);
      return;
  }
  // Ensure event and event.target exist to prevent errors
  if (!event || !event.target) {
       console.warn(`handleAnswerSelect received invalid event object for questionId: ${questionId}`);
       return; // Avoid errors if event is malformed
  }

  const isMC = currentQuestion.question_type === 'MC';
  // *** Always extract value from event.target.value ***
  const newValue = event.target.value;

  // console.log(`handleAnswerSelect: QID=${questionId}, Type=${isMC ? 'MC' : 'Input'}, Value='${newValue}'`); // Log extracted value

  // Update local state immediately for responsiveness
  setSelectedAnswers(prev => ({ ...prev, [questionId]: newValue }));

  if (!isMC) {
      // Update userInput state only for non-MC questions
      setUserInput(newValue); // Now newValue is the actual input string/number
      validateInput(newValue); // Validate on change for immediate feedback in UI
  }
  // Note: No need for an 'else' here to clear userInput for MC,
  // as the userInput state is only passed to the MultipleChoiceQuestion component
  // when isMCQuestion is false. See the 'userInput' prop further down.

  // Update refs
  currentQuestionIdRef.current = questionId;
  currentAnswerRef.current = newValue;

  // Debounce the actual save operation (validation happens inside debouncedSave)
  setSaveStatuses(prev => ({ ...prev, [questionId]: 'syncing' })); // Optimistic UI: show syncing
  debouncedSave(questionId, newValue);
};

    // Handle blur for fill-in-the-blank (final validation and save trigger)
    const handleInputBlur = () => {
        const questionId = currentQuestionIdRef.current;
        const value = userInput; // Use the state value

        if (questionId && validateInput(value)) {
            // Cancel any pending debounced save for this question
            debouncedSave.cancel();
            // Immediately save the validated answer on blur
            saveCurrentAnswer(questionId, value);
        } else if (questionId) {
            // Handle case where input is invalid on blur
            console.warn(`Invalid input for question ${questionId} on blur:`, value);
            // Optionally: clear the answer or keep the invalid input shown?
            // saveCurrentAnswer(questionId, ''); // Option: Clear answer if invalid on blur
        }
    };

// Helper function to parse duration string (HH:MM:SS.ffffff) into milliseconds
const parseDuration = (durationString) => {
  if (!durationString || typeof durationString !== 'string') {
      console.warn("Invalid duration string provided:", durationString);
      return 0;
  }
  const parts = durationString.split(':');
  if (parts.length !== 3) {
      console.warn("Duration string format is not HH:MM:SS.ffffff:", durationString);
      return 0; // Invalid format
  }
  const secondsParts = parts[2].split('.');
  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);
  const seconds = parseInt(secondsParts[0], 10);
  // Microseconds are tricky, handle potential missing fractional part
  const microseconds = secondsParts.length > 1 ? parseInt(secondsParts[1].padEnd(6, '0'), 10) : 0;

  if (isNaN(hours) || isNaN(minutes) || isNaN(seconds) || isNaN(microseconds)) {
      console.warn("Failed to parse numeric values from duration:", durationString);
      return 0; // Parsing failed
  }

  // Calculate total milliseconds from hours, minutes, seconds, and microseconds
  const totalMilliseconds = (hours * 3600 + minutes * 60 + seconds) * 1000 + Math.floor(microseconds / 1000);
  return totalMilliseconds;
};
    // Fetch initial exam status and saved answers
    const fetchExamStatus = useCallback(async () => {
        // console.log("Fetching exam status...");
        setLoading(true);
        const localStorageKey = getLocalStorageKey(studentexamId);

        try {
            // Fetch details (includes server answers)
            const { exam, savedAnswers } = await fetchExamDetails(studentexamId, navigate);
            // console.log('exam',exam)
            setCurrentExam(exam);
            // console.log("Exam details received:", exam);
             // --- Merge Local and Server Answers ---
             const localAttemptData = JSON.parse(localStorage.getItem(localStorageKey) || '{}');
             const initialSelectedAnswers = {};
             const initialSaveStatuses = {};
             const questions = exam?.current_module?.questions || [];
             const itemsToSyncOnLoad = []; // Track items needing sync

             questions.forEach(question => {
                 const questionId = question.id;
                 const serverAnswer = savedAnswers.find(a => a.question === questionId);
                 const localAnswerData = localAttemptData[questionId];
                 let finalAnswer = undefined;
                 let finalStatus = 'unsaved'; // Default status

                 if (localAnswerData) {
                     // Local data exists
                     if (localAnswerData.status === 'unsynced') {
                         finalAnswer = localAnswerData.answer;
                         finalStatus = 'unsynced';
                         itemsToSyncOnLoad.push({ questionId, data: localAnswerData });
                        //  console.log(`Question ${questionId}: Loaded unsynced local answer.`);
                     } else { // status === 'synced' or 'error'
                         // Compare timestamps if server answer exists
                         if (serverAnswer && new Date(serverAnswer.timestamp) > new Date(localAnswerData.timestamp)) {
                             finalAnswer = serverAnswer.answer; // Server is newer
                             finalStatus = 'synced'; // Assume server data is synced
                             // Update local storage to reflect server state
                             localAttemptData[questionId] = { answer: serverAnswer.answer, status: 'synced', timestamp: serverAnswer.timestamp };
                            //  console.log(`Question ${questionId}: Server answer is newer than local synced. Using server version.`);
                         } else {
                             finalAnswer = localAnswerData.answer; // Local synced is newer or same, or no server answer
                             finalStatus = localAnswerData.status; // Keep local status ('synced' or 'error')
                            //  console.log(`Question ${questionId}: Loaded local answer with status '${finalStatus}'.`);
                         }
                     }
                 } else if (serverAnswer) {
                     // No local data, but server data exists
                     finalAnswer = serverAnswer.answer;
                     finalStatus = 'synced'; // Assume server data is synced
                     // Add to local storage
                     localAttemptData[questionId] = { answer: serverAnswer.answer, status: 'synced', timestamp: serverAnswer.timestamp || Date.now() };
                    //  console.log(`Question ${questionId}: Loaded synced answer from server.`);
                 } else {
                     // No local or server data
                    //  console.log(`Question ${questionId}: No answer found locally or on server.`);
                 }

                 initialSelectedAnswers[questionId] = finalAnswer;
                 initialSaveStatuses[questionId] = finalStatus;
             });

             // Write potentially updated local data back
             localStorage.setItem(localStorageKey, JSON.stringify(localAttemptData));

             // Set React state
             setSelectedAnswers(initialSelectedAnswers);
             setSaveStatuses(initialSaveStatuses);

             // --- Sync Items Found During Load ---
             if (itemsToSyncOnLoad.length > 0) {
                // console.log(`Attempting to sync ${itemsToSyncOnLoad.length} unsynced items found on load...`);
                // Trigger sync for each item (consider batching if API supports it)
                itemsToSyncOnLoad.forEach(item => {
                    dispatch(autosaveAnswer(item.questionId, item.data.answer, studentexamId))
                        .then(() => {
                            // console.log(`Successfully synced item ${item.questionId} from load.`);
                            // Update localStorage status to 'synced' after successful sync
                            let currentAttemptData = JSON.parse(localStorage.getItem(localStorageKey) || '{}');
                            if (currentAttemptData[item.questionId] && currentAttemptData[item.questionId].timestamp === item.data.timestamp) {
                                currentAttemptData[item.questionId].status = 'synced';
                                localStorage.setItem(localStorageKey, JSON.stringify(currentAttemptData));
                                setSaveStatuses(prev => ({ ...prev, [item.questionId]: 'synced' }));
                            }
                        })
                        .catch(err => {
                            console.error(`Failed to sync item ${item.questionId} from load:`, err);
                            setSaveStatuses(prev => ({ ...prev, [item.questionId]: 'error' }));
                        });
                });
            }

            // console.log('exam printing')
            // --- Update Timers and Break Status ---
            // Inside the fetchExamStatus function, within the try block:

            // --- Update Timers and Break Status ---
            if (exam.on_break) {
              const breakEndTimeMs = exam.break_end_time ? new Date(exam.break_end_time).getTime() : 0;
              setBreakendTime(breakEndTimeMs);
              setOnBreak(true);
              setEndTime(0); // No module timer during break
              // console.log("Exam is on break. Break ends at:", exam.break_end_time);
          } else {
              // *** Calculate Module End Time *** << THIS IS THE MODIFIED PART
              let calculatedEndTimeMs = 0;
              if (exam.module_start_time && exam.current_module?.time_limit) {
                  try {
                      const startTime = new Date(exam.module_start_time);
                      const durationMs = parseDuration(exam.current_module.effective_time_limit);
                      if (!isNaN(startTime.getTime()) && durationMs > 0) {
                          calculatedEndTimeMs = startTime.getTime() + durationMs;
                          // console.log(`Calculated Module End Time: Start=<span class="math-inline">\{exam\.module\_start\_time\}, Duration\=</span>{exam.current_module.time_limit} (<span class="math-inline">\{durationMs\}ms\), End\=</span>{new Date(calculatedEndTimeMs).toISOString()}`);
                      } else {
                           // Log specific parsing failures if possible
                          if (isNaN(startTime.getTime())) console.error("Invalid module_start_time format:", exam.module_start_time);
                          if (durationMs <= 0) console.error("Invalid or zero current_module.time_limit:", exam.current_module.time_limit);
                      }
                  } catch (e) {
                      console.error("Error calculating module end time:", e);
                  }
              } else {
                  console.warn("Missing module_start_time or current_module.time_limit in exam data. Cannot calculate end time.");
                  // Fallback or default behavior if calculation fails? Set to 0 for now.
                  calculatedEndTimeMs = 0;
              }

              setEndTime(calculatedEndTimeMs); // Set the calculated end time
              setOnBreak(false);
              setBreakendTime(0);
              // console.log("Exam is active. Calculated module end time (ms):", calculatedEndTimeMs);
          }
            // --- Set Initial Question ---
            if (exam.current_module?.questions?.length > 0) {
                const firstQuestionId = exam.current_module.questions[0].id;
                setCurrentQuestionIndex(0); // Reset to first question
                currentQuestionIdRef.current = firstQuestionId;
                currentAnswerRef.current = initialSelectedAnswers[firstQuestionId];
                // Set initial userInput for fill-in-the-blank
                 const firstQuestion = exam.current_module.questions[0];
                 if (firstQuestion.question_type !== 'MC') {
                    setUserInput(initialSelectedAnswers[firstQuestionId] || '');
                 } else {
                    setUserInput(''); // Clear for MC questions
                 }
            } else {
                console.warn("Current module has no questions.");
            }

        } catch (err) {
            console.error('Error fetching exam status:', err);
            handleErrorRef.current(err, 'Failed to load exam details. Please try again.'); // Use ref here
            // if (err?.response?.status === 403) {
            //     setTimeout(() => navigate(`/exam-details/${studentexamId}`), 2000);
            // }
        } finally {
            setLoading(false);
            // console.log("Finished fetching exam status.");
        }
    }, [studentexamId, navigate, dispatch, getLocalStorageKey, handleErrorRef]); // Added getLocalStorageKey, handleErrorRef

// Handle moving to the next module or completing the exam
const handleModuleTransition = useCallback(async (manual = false) => {
  // console.log(`handleModuleTransition triggered (${manual ? 'manual' : 'auto'})...`);
  setSnackbarOpen(false); // Close any previous informational snackbar
  // setSnackbarMessage(''); // Clear previous message
  const localStorageKey = getLocalStorageKey(studentexamId);
  let syncSuccessful = true; // Assume success unless proven otherwise
  transitionIntentionRef.current = '';
  // --- Final Sync Attempt Before Transition ---
  const findUnsyncedItems = () => {
      const attemptData = JSON.parse(localStorage.getItem(localStorageKey) || '{}');
      return Object.entries(attemptData)
          .filter(([_, data]) => data?.status === 'unsynced')
          .map(([questionId, data]) => ({ questionId, data }));
  };

  const syncAllUnsyncedItems = async (itemsToSync) => {
      const syncPromises = itemsToSync.map(item =>
          dispatch(autosaveAnswer(item.questionId, item.data.answer, studentexamId))
              .then(() => {
                  // Update local storage on successful sync
                  let currentAttemptData = JSON.parse(localStorage.getItem(localStorageKey) || '{}');
                  if (currentAttemptData[item.questionId]?.timestamp === item.data.timestamp) {
                      currentAttemptData[item.questionId].status = 'synced';
                      localStorage.setItem(localStorageKey, JSON.stringify(currentAttemptData));
                      setSaveStatuses(prev => ({ ...prev, [item.questionId]: 'synced' }));
                  }
                  return { questionId: item.questionId, status: 'fulfilled' };
              })
              .catch(error => {
                  setSaveStatuses(prev => ({ ...prev, [item.questionId]: 'error' }));
                  return { questionId: item.questionId, status: 'rejected', reason: error };
              })
      );
      return Promise.allSettled(syncPromises);
  };

  const unsyncedItems = findUnsyncedItems();
  if (unsyncedItems.length > 0) {
      // console.log(`Found ${unsyncedItems.length} unsynced items. Attempting final sync...`);
      setSnackbarMessage("Saving final changes...");
      setSnackbarOpen(true); // Show "Saving..." snackbar
      const results = await syncAllUnsyncedItems(unsyncedItems);
      const failedSyncs = results.filter(result => result.status === 'rejected');
      setSnackbarOpen(false); // Close "Saving..." snackbar
      await new Promise(resolve => setTimeout(resolve, 100)); // Optional small delay

      if (failedSyncs.length > 0 && !navigator.onLine) {
          console.error('Final sync failed while offline. Module transition aborted.');
          handleErrorRef.current(
              failedSyncs[0].reason || new Error('Sync failed offline'),
              "Cannot submit module. Please check your internet connection to save all changes and try again. Your work is saved locally."
          );
          syncSuccessful = false; // Mark sync as failed, proceed to final else block
      } else if (failedSyncs.length > 0) {
          console.warn(`Some final sync attempts failed (${failedSyncs.length}), but proceeding as network seems online.`);
          handleErrorRef.current(null, `Warning: Could not save all changes for ${failedSyncs.length} questions. Proceeding anyway.`, 'warning');
          // Allow transition to continue despite partial sync failure if online
      } else {
          // console.log('Final sync successful.');
      }
  } else {
      // console.log('No unsynced items found before transition.');
  }
  // --- End Final Sync ---

  // --- Call Backend to Move Module (only if sync didn't force an abort) ---
  if (syncSuccessful) {
      try {
          // *** SET STATES TO INDICATE START OF TRANSITION ***
          setLoading(true); // Set loading true
          setSnackbarMessage("Transitioning to next section..."); // Set message

          setIsTransitioning(true); // Set transitioning true
          // console.log('mark snack bar open TRUE')
          setSnackbarOpen(true); // Show "Transitioning..." snackbar
          // console.log('>>> Set snackbar OPEN for Transitioning...');

          // console.log('Calling moveNextModule API...');
          const nextModuleResponse = await moveNextModule(studentexamId, manual);
          // console.log('moveNextModule response:', nextModuleResponse);

          if (nextModuleResponse?.status === 'C') { // Check if status is Completed
              // console.log('Exam completed successfully. Navigating shortly...');
              localStorage.removeItem(localStorageKey);

              // Set final states before delayed navigation
              setLoading(false); // Turn off loading overlay
              setIsTransitioning(false); // Turn off transitioning flag
              setSnackbarMessage("Exam completed! Redirecting..."); // Update message
              // setSnackbarOpen(true); // Ensure completion snackbar is visible

              // Navigate after a delay
              navigate(`/exam-details/${studentexamId}`);
              return; // IMPORTANT: Return early to prevent fetchExamStatus call
          } else {
              // Transition successful (to next module or break)
              // console.log('Transition successful. Resetting SOME UI state and fetching new status...',snackbarOpen);
              
              // Reset only UI elements not handled by fetchExamStatus data
              setCalculatorVisible(false);
              setReferenceVisible(false);
              setIsCalculatorExpanded(false);
              setShowReviewPage(false);
              // DONT reset data state like answers, index, etc. - fetchExamStatus handles these

              // *** FETCH THE NEW STATE - fetchExamStatus will handle final state clearing ***
              // *** FETCH THE NEW STATE - fetchExamStatus will handle final state clearing ***
              await fetchExamStatus();

              // *** Add Logging Here ***
              // console.log(">>> Reached code AFTER await fetchExamStatus().");
              // console.log(`>>> SNACK BAR MESSAGE   value: "${snackbarMessage}"`);
              // console.log(`>>> SNACK BAR MESSAGE   value: "${snackbarOpen}"`);

              // *************************

              if (transitionIntentionRef.current === "Transitioning to next section...") {
                // console.log(">>> Condition is TRUE. Setting timeout..."); // Log that condition passed
                  // Use a short timeout to ensure it renders before closing
                  const timer = setTimeout(() => {
                      // *** Log inside the timeout ***
                      // console.log(">>> INSIDE TIMEOUT: Closing 'Transitioning...' snackbar."); // Modified log to be distinct
                      // *****************************
                      setSnackbarOpen(false);

                      // Optionally clear the message *after* closing animation
                      // setTimeout(() => setSnackbarMessage(''), 300); // Adjust timing if needed
                  }, 150); // Short delay (e.g., 150ms) after loading finishes
                  // No cleanup needed for this short timeout, but good practice for longer ones
              } else {
                  // *** Add Logging if condition failed ***
                  // console.log(">>> Condition is FALSE. Skipping timeout.");
                  // ************************************
              }

              // Modified log to confirm this section finishes
              // console.log(">>> handleModuleTransition: Logic AFTER fetchExamStatus processed.");
          }

      } catch (error) {
          console.error('!!!!!! Error during moveNextModule API call:', error);
          // *** ON ERROR: Explicitly turn off transition indicators ***
          setLoading(false);
          setIsTransitioning(false);
          transitionIntentionRef.current = ''; // Reset ref after use

          if (snackbarMessage === "Transitioning to next section...") {
            setSnackbarOpen(false);
       }
          // --- Determine error message (Your existing logic) ---
          let displayMessage = "An error occurred while trying to move to the next section. Please try again.";
          let displaySeverity = 'error';
          if (error.response?.data?.error?.code === 'NO_ANSWERS') {
              displayMessage = error.response.data.error.message || "At least one question must be answered before transitioning.";
              displaySeverity = 'warning';
              console.warn("Transition blocked by backend: NO_ANSWERS");
          } else if (!navigator.onLine) {
              displayMessage = "You appear to be offline. Could not move to the next section.";
              displaySeverity = 'error';
              console.warn("Transition failed: Offline detected.");
          } // Add more specific error checks if needed
          // --- Call the error handler ---
          console.error('!!!!!! Calling handleErrorRef.current NOW !!!!!!');
          handleErrorRef.current(error, displayMessage, displaySeverity); // Show error snackbar
          console.error('!!!!!! AFTER calling handleErrorRef.current !!!!!!');

      } finally {
           // *** FINALLY BLOCK IS NOW MINIMAL ***
           // State cleanup (loading, isTransitioning, snackbarOpen) is handled by:
           // 1. The fetchExamStatus() call in the success path.
           // 2. The explicit cleanup in the 'catch' block on error.
           // 3. The explicit cleanup in the 'status === C' completion path.
          //  console.log('Exiting handleModuleTransition finally block (minimal action).');
      }
  } else {
      // Handle case where sync failed and aborted the transition
      setLoading(false); // Ensure loading is off if sync failed
      setIsTransitioning(false); // Ensure transition state is off
      // console.log("Transition aborted due to sync failure.");
      transitionIntentionRef.current = ''; // Reset ref after use

  }

// *** UPDATE Dependency Array - Ensure fetchExamStatus is included ***
// Add any other state variables or functions this callback relies on.
}, [studentexamId, navigate, dispatch, getLocalStorageKey, handleErrorRef, fetchExamStatus, snackbarMessage]); // <-- Added snackbarMessage
// Sync server time and check for state mismatches (Polling Function)
    const syncAndCheckState = useCallback(async () => {
      // console.log('[Sync] Attempting sync and check state...');
      if (isSyncing.current || loading || !studentexamId) {
          return;
      }
      isSyncing.current = true;

      try {
          // *** MODIFICATION HERE: Call fetchExamTime directly, without dispatch ***
          // Assuming fetchExamTime is an async function returning the server state object
          // console.log('[Sync] Calling fetchExamTime directly for studentexamID:', studentexamId); // Added log
          const serverState = await fetchExamTime(studentexamId);

          if (!serverState || !serverState.server_time) {
              // Added check for null/undefined serverState
              console.error('[Sync] Invalid or null state received from fetchExamTime.');
               // Optionally handle this error more gracefully, maybe retry?
               // For now, we throw to be caught below.
              throw new Error("Received invalid state from server sync.");
          }

          // ** DETAILED LOGGING FOR DEBUGGING TIMEOUT **
          // console.log('[Sync] Server State Received:', {
          //     server_time: serverState.server_time,
          //     end_time: serverState.end_time, // This is the AUTHORITATIVE end time from the server for the current module
          //     break_end_time: serverState.break_end_time,
          //     on_break: serverState.on_break,
          //     exam_status: serverState.exam_status,
          //     current_module_id: serverState.current_module_id,
          // });

          const serverTimeMs = new Date(serverState.server_time).getTime();
          // *** Use the end_time directly from fetchExamTime response ***
          const serverEndTimeMs = serverState.end_time ? new Date(serverState.end_time).getTime() : 0;
          const serverBreakEndTimeMs = serverState.break_end_time ? new Date(serverState.break_end_time).getTime() : 0;

          let stateMismatch = false;
          let isTimedOut = false;

          // 1. Check Exam Completion
          if (serverState.exam_status === 'C' && currentExam?.status !== 'C') {
              console.warn('[Sync] Server indicates exam completed. Forcing cleanup and navigation.');
              localStorage.removeItem(getLocalStorageKey(studentexamId));
              isSyncing.current = false;
              // navigate('/my-exams');
              navigate(`/exam-details/${studentexamId}`)
              return;
          }

          // 2. Compare On Break Status
          if (serverState.on_break !== onBreak) {
              stateMismatch = true;
              // console.log(`[Sync] OnBreak state mismatch: Server=${serverState.on_break}, Frontend=${onBreak}`);
          }

          // 3. Compare Module ID
          // Ensure currentExam and current_module exist before accessing id
          const currentFrontendModuleId = currentExam?.current_module?.id;
          if (!serverState.on_break && currentExam && currentFrontendModuleId !== undefined && serverState.current_module_id !== currentFrontendModuleId) {
              stateMismatch = true;
              // console.log(`[Sync] Module ID mismatch: Server=${serverState.current_module_id}, Frontend=${currentFrontendModuleId}`);
          }

          // 4. Check Clock Skew (Optional Warning)
          const clientTimeMs = Date.now();
          const clockSkewSeconds = Math.abs(serverTimeMs - clientTimeMs) / 1000;
          if (clockSkewSeconds > 60) {
              console.warn(`[Sync] Significant clock skew detected: ${clockSkewSeconds.toFixed(1)}s difference.`);
          }

          // 5. *** Check for Module Timeout ***
          if (serverState.exam_status === 'P' && !serverState.on_break && serverEndTimeMs > 0 && serverTimeMs >= serverEndTimeMs) {
              isTimedOut = true;
              // console.log(`[Sync] TIMEOUT DETECTED: Server time (${serverState.server_time}) >= Module end time (${serverState.end_time}).`);
          } else {
              // console.log(`[Sync] Timeout check: ongoing=${serverState.exam_status === 'ongoing'}, !on_break=${!serverState.on_break}, endTimeValid=${serverEndTimeMs > 0}, timePassed=${serverTimeMs >= serverEndTimeMs}`);
          }

          // --- Take Action Based on Checks ---
          if (isTimedOut && currentExam?.time_limit) {
              console.warn('[Sync] Module timed out according to server. Triggering module transition...');
              isSyncing.current = false;
              debouncedSave.flush(); // Force save any pending changes
              await handleModuleTransition(false); // manual = false
              return;

          } else if (stateMismatch) {
              console.warn('[Sync] Server state mismatch detected (not timeout). Forcing full state refresh via fetchExamStatus.');
              isSyncing.current = false;
              fetchExamStatus(); // Fetch full status to resolve mismatch
              return;

          } else {
              // State is consistent, update local timers if needed based on the authoritative server state
               if (endTime !== serverEndTimeMs && serverEndTimeMs > 0) { // Only update if valid and different
                  //  console.log(`[Sync] Updating local endTime from ${endTime} to ${serverEndTimeMs}`);
                   setEndTime(serverEndTimeMs);
               }
               if (breakendTime !== serverBreakEndTimeMs) {
                  //  console.log(`[Sync] Updating local breakendTime from ${breakendTime} to ${serverBreakEndTimeMs}`);
                   setBreakendTime(serverBreakEndTimeMs);
               }
               if (onBreak !== serverState.on_break) {
                  //  console.log(`[Sync] Updating local onBreak from ${onBreak} to ${serverState.on_break}`);
                   setOnBreak(serverState.on_break);
               }
          }

      } catch (error) {
                 // *** MODIFIED ERROR HANDLING for syncAndCheckState ***
        console.error('[Sync] Error during periodic state sync:', error);

        // 1. Ignore the specific error we threw for invalid state responses
        if (error.message === "Received invalid state from server sync.") {
            console.warn("[Sync] Handled expected invalid state error during sync. No user notification shown.");
            // Don't show a user-facing error for this internal check failure
        }
        // 2. Check if the browser reports being offline
        else if (!navigator.onLine) {
            console.warn("[Sync] Sync check failed while offline.");
            // Show a less intrusive 'info' message for periodic background failures
            // Avoid flooding the user if polling keeps failing while offline
            // Note: You might want logic here or in useErrorHandler to show this only once
            // or less frequently if the offline state persists.
            handleErrorRef.current(
                error,
                "You appear to be offline. Cannot sync timer with server; local timer may differ slightly. ",
                'info' // Use 'info' or 'warning' - 'info' might be less annoying for polling
            );
        }
        // 3. Handle other online errors (e.g., server errors)
        else {
            // Show a 'warning' for other unexpected sync errors
            handleErrorRef.current(
                error,
                'Could not sync timer with server; local timer may differ slightly. Please check connection.', // More specific than generic 'Error syncing'
                'warning'
            );
        }
        // *** END MODIFIED HANDLING ***
      } finally {
          isSyncing.current = false;
      }
  // Keep dependencies minimal; functions called inside should manage their own dependencies via useCallback
  }, [studentexamId, loading, currentExam, onBreak, navigate, fetchExamStatus, getLocalStorageKey, handleErrorRef, endTime, breakendTime, debouncedSave, handleModuleTransition]); // Added handleModuleTransition back as it's called directly now

    

    // Handle frontend timer timeout
    const handleTimeout = useCallback(async () => {
        // console.log('[Timeout] Frontend countdown timeout detected. Triggering final sync and check...');
        // Don't call transition directly. Let syncAndCheckState verify with the server.
        await syncAndCheckState();
        // syncAndCheckState will call handleModuleTransition if the server confirms timeout.
        // console.log('[Timeout] Sync check initiated.');
    }, [syncAndCheckState]);


    // Navigation Handlers
    const handleNextQuestion = () => {
        debouncedSave.flush(); // Ensure latest answer is saved before navigating
        const currentQuestionId = currentExam?.current_module?.questions[currentQuestionIndex]?.id;
        // saveCurrentAnswer(currentQuestionId, selectedAnswers[currentQuestionId]); // Save immediately

        if (currentQuestionIndex < currentExam.current_module.questions.length - 1) {
            const nextIndex = currentQuestionIndex + 1;
            setCurrentQuestionIndex(nextIndex);
            // Update refs and userInput for the new question
             const nextQuestion = currentExam.current_module.questions[nextIndex];
             currentQuestionIdRef.current = nextQuestion.id;
             currentAnswerRef.current = selectedAnswers[nextQuestion.id];
             if (nextQuestion.question_type !== 'MC') {
                setUserInput(selectedAnswers[nextQuestion.id] || '');
                setInputError(''); // Clear potential error from previous question
             } else {
                setUserInput('');
             }
        } else {
            openReviewPage(); // Open review page if it's the last question
        }
    };

    const handlePreviousQuestion = () => {
        debouncedSave.flush(); // Ensure latest answer is saved before navigating
        const currentQuestionId = currentExam?.current_module?.questions[currentQuestionIndex]?.id;
        // saveCurrentAnswer(currentQuestionId, selectedAnswers[currentQuestionId]); // Save immediately

        if (currentQuestionIndex > 0) {
            const prevIndex = currentQuestionIndex - 1;
            setCurrentQuestionIndex(prevIndex);
            // Update refs and userInput for the new question
             const prevQuestion = currentExam.current_module.questions[prevIndex];
             currentQuestionIdRef.current = prevQuestion.id;
             currentAnswerRef.current = selectedAnswers[prevQuestion.id];
             if (prevQuestion.question_type !== 'MC') {
                setUserInput(selectedAnswers[prevQuestion.id] || '');
                setInputError(''); // Clear potential error from previous question
             } else {
                setUserInput('');
             }
        }
    };

     const handleSelectQuestion = (index) => {
        if (index === currentQuestionIndex) return; // No change

        debouncedSave.flush(); // Ensure latest answer is saved before navigating
        const currentQuestionId = currentExam?.current_module?.questions[currentQuestionIndex]?.id;
        // saveCurrentAnswer(currentQuestionId, selectedAnswers[currentQuestionId]); // Save immediately

        setCurrentQuestionIndex(index);
        // Update refs and userInput for the selected question
         const newQuestion = currentExam.current_module.questions[index];
         currentQuestionIdRef.current = newQuestion.id;
         currentAnswerRef.current = selectedAnswers[newQuestion.id];
         if (newQuestion.question_type !== 'MC') {
            setUserInput(selectedAnswers[newQuestion.id] || '');
            setInputError(''); // Clear potential error from previous question
         } else {
            setUserInput('');
         }
         setIsMinimapVisible(false); // Close minimap after selection
         setShowReviewPage(false); // Close review page if open
    };

    // Review Page Handlers
    const openReviewPage = () => {
        debouncedSave.flush(); // Save current answer before opening review
        const currentQuestionId = currentExam?.current_module?.questions[currentQuestionIndex]?.id;
        // saveCurrentAnswer(currentQuestionId, selectedAnswers[currentQuestionId]);
        setShowReviewPage(true);
    };

    const closeReviewPage = () => {
        // No need to save again here, was saved on open or during interaction
        setShowReviewPage(false);
    };

    // Break Handlers
    const handleSkipBreak = useCallback(async () => {
        // console.log("Attempting to skip break...");
        setLoading(true); // Show loading indicator
        try {
            await skipBreak(studentexamId);
            // console.log("Break skipped successfully. Fetching updated status...");
            // setOnBreak(false); // Optimistic update (optional)
            await fetchExamStatus(); // Fetch new status to confirm break ended and get module details
        } catch (error) {
            console.error('Error skipping break:', error);
            handleErrorRef.current(error, 'Failed to skip break. Please try again.');
            setLoading(false); // Turn off loading on error
        }
        // setLoading(false) will be handled by fetchExamStatus's finally block on success
    }, [studentexamId, fetchExamStatus, handleErrorRef]); // Added handleErrorRef


    // UI Toggles
    const toggleCalculator = () => setCalculatorVisible(!calculatorVisible);
    const toggleReference = () => setReferenceVisible(!referenceVisible);
    const toggleMinimap = () => setIsMinimapVisible(!isMinimapVisible);
    const setMiniMapFalse = () => setIsMinimapVisible(false);
    const toggleMarkForReview = (questionId) => {
        setMarkedForReview(prev => ({ ...prev, [questionId]: !prev[questionId] }));
        // Optionally: trigger a save or sync action for review status if needed by backend
    };
     const toggleCrossedOut = (questionId, choiceIndex) => {
        setCrossedOut(prev => {
            const currentChoices = prev[questionId] || Array(currentExam?.current_module?.questions[currentQuestionIndex]?.choices?.length || 4).fill(false);
            const newChoices = [...currentChoices];
            newChoices[choiceIndex] = !newChoices[choiceIndex]; // Toggle the specific choice
            return { ...prev, [questionId]: newChoices };
        });
    };
    // This seems redundant if toggleCrossedOut handles both crossing and uncrossing?
    const toggleTrueCrossedOut = (questionId, choiceIndex) => {
      setCrossedOut(prev => {
        // Check if there is already an entry for this question; if not, initialize with an array of false values.
        const currentChoices = prev[questionId] || Array.from({ length: 4 }, () => false);
    // console.log(currentChoices);
        // Return the new state with the specified choiceIndex set to true, while other choices retain their current state.
        return {
          ...prev,
          [questionId]: currentChoices.map((item, index) => 
            index === choiceIndex ? false : item  // Set the element at choiceIndex to true, others are unchanged.
          )
        };
      });
    };
    

    // Utility Checkers
    const isQuestionAnswered = useCallback((questionId) => {
        const answer = selectedAnswers[questionId];
        return answer !== undefined && answer !== null && answer !== '';
    }, [selectedAnswers]);

    const isMarkedReview = useCallback((questionId) => {
        return markedForReview[questionId] === true;
    }, [markedForReview]);

    const isMathModule = useCallback(() => {
        return currentExam?.current_module?.mathMode === true;
    }, [currentExam]);
    useEffect(() => {
        const preventCopyPaste = (event) => {
            // console.log(`Blocked event: ${event.type}`);
            handleErrorRef.current(
                null, // No actual error object
                'Copying, cutting, and pasting are disabled during the exam.',
                'warning', // Or 'info'
                3000 // Shorter duration
            );
            event.preventDefault();
        };
    
        const preventContextMenu = (event) => {
            // console.log('Blocked context menu');
             handleErrorRef.current(
                null,
                'Right-clicking is disabled during the exam.',
                'warning',
                 3000
            );
            event.preventDefault();
        };
    
        // Target the main content area. You might need a more specific selector
        // if FocusLock interferes, or apply directly to the content wrapper div.
        const targetElement = document.body; // Or a more specific container like '#sandbox' if stable
    
        targetElement.addEventListener('copy', preventCopyPaste);
        targetElement.addEventListener('cut', preventCopyPaste);
        // targetElement.addEventListener('paste', preventCopyPaste); // Decide if you want to block paste too
        targetElement.addEventListener('contextmenu', preventContextMenu);
    
        // Add the CSS class for user-select dynamically (optional but cleaner)
        // Make sure you have this CSS defined globally or scoped:
        // .no-select { user-select: none; -webkit-user-select: none; -moz-user-select: none; -ms-user-select: none; }
        const contentWrapper = document.getElementById('sandbox'); // Use a stable ID for your main content area
        if (contentWrapper) {
             contentWrapper.classList.add('no-select');
        }
    
    
        // Cleanup function to remove listeners and class when component unmounts
        return () => {
            targetElement.removeEventListener('copy', preventCopyPaste);
            targetElement.removeEventListener('cut', preventCopyPaste);
            // targetElement.removeEventListener('paste', preventCopyPaste);
            targetElement.removeEventListener('contextmenu', preventContextMenu);
             if (contentWrapper) {
                contentWrapper.classList.remove('no-select');
            }
        };
        // Rerun if error handler changes (though unlikely)
    }, [handleErrorRef]); // Add dependencies if needed, handleErrorRef ensures the latest handler is used
    

    // --- useEffect Hooks ---
    useEffect(() => {
      // This runs on mount AND whenever errorState.open changes value
      // console.log(`## useEffect [errorState.open] ## errorState.open changed to: ${errorState.open}`);
      if (errorState.open) {
         console.warn("## useEffect [errorState.open] ## Detected errorState became true!");
      }
    }, [errorState.open]); // Dependency array includes errorState.open
    // Update handleError ref when hook updates
    useEffect(() => {
        handleErrorRef.current = handleError;
    }, [handleError]);

    // Initial fetch on mount
    useEffect(() => {
        fetchExamStatus();
    }, [fetchExamStatus]); // fetchExamStatus is stable due to useCallback

    // Polling interval for sync
    useEffect(() => {
        if (loading || !currentExam || currentExam.status === 'C' || !isPageVisible || onBreak) {
            // console.log('[Polling] Skipping setup: Conditions not met.');
            return; // Don't poll if loading, completed, hidden, or on break
        }
        // console.log('[Polling] Setting up polling interval...');
        const intervalId = setInterval(syncAndCheckState, POLLING_INTERVAL);
        return () => {
            // console.log('[Polling] Clearing polling interval.');
            clearInterval(intervalId);
        };
    }, [loading, currentExam, isPageVisible, onBreak, syncAndCheckState]); // Added onBreak

    // Visibility change handler (triggers immediate sync on focus)
    useEffect(() => {
        const handleVisibilityChange = () => {
            const visible = !document.hidden;
            setIsPageVisible(visible);
            if (visible) {
                // console.log('[Visibility] Page became visible, triggering immediate state sync...');
                syncAndCheckState(); // Sync immediately when tab becomes visible
            } else {
                // console.log('[Visibility] Page became hidden.');
                debouncedSave.flush(); // Save any pending changes when tab is hidden
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [syncAndCheckState, debouncedSave]); // Added debouncedSave

    // Beforeunload prompt
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            // Consider showing only if there are unsynced changes
             const localStorageKey = getLocalStorageKey(studentexamId);
             const attemptData = JSON.parse(localStorage.getItem(localStorageKey) || '{}');
             const hasUnsynced = Object.values(attemptData).some(d => d?.status === 'unsynced');

             if (hasUnsynced) {
                e.preventDefault();
                e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
                return e.returnValue;
             }
             // If no unsynced changes, allow navigation without prompt
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [studentexamId, getLocalStorageKey]); // Added dependencies

     // Network online handler (attempt to sync unsynced items)
     useEffect(() => {
        const handleOnline = () => {
            // console.log('Network connection restored. Checking for unsynced answers...');
            const localStorageKey = getLocalStorageKey(studentexamId);
            const attemptData = JSON.parse(localStorage.getItem(localStorageKey) || '{}');
            const itemsToSync = Object.entries(attemptData)
                .filter(([_, data]) => data?.status === 'unsynced')
                .map(([questionId, data]) => ({ questionId, data }));

            if (itemsToSync.length > 0) {
                // console.log(`Attempting to sync ${itemsToSync.length} unsynced items on network recovery...`);
                itemsToSync.forEach(item => {
                    dispatch(autosaveAnswer(item.questionId, item.data.answer, studentexamId))
                        .then(() => { /* Update localStorage status to 'synced' */
                            //  console.log(`Successfully synced item ${item.questionId} on recovery.`);
                             let currentAttemptData = JSON.parse(localStorage.getItem(localStorageKey) || '{}');
                             if (currentAttemptData[item.questionId]?.timestamp === item.data.timestamp) {
                                 currentAttemptData[item.questionId].status = 'synced';
                                 localStorage.setItem(localStorageKey, JSON.stringify(currentAttemptData));
                                 setSaveStatuses(prev => ({ ...prev, [item.questionId]: 'synced' }));
                             }
                        })
                        .catch(err => {
                            console.error(`Failed to sync item ${item.questionId} on recovery:`, err);
                            setSaveStatuses(prev => ({ ...prev, [item.questionId]: 'error' }));
                         });
                });
            } else {
                // console.log("No unsynced items found on network recovery.");
            }
        };
        window.addEventListener('online', handleOnline);
        return () => window.removeEventListener('online', handleOnline);
    }, [studentexamId, dispatch, getLocalStorageKey]); // Added dependencies


    // Cancel debounce on unmount
    useEffect(() => {
        return () => {
            debouncedSave.cancel();
        };
    }, [debouncedSave]);


    // --- Render Logic ---

    // Loading state
    if (loading && !currentExam) return <LoadingOverlay />; // Show overlay only on initial load

    // Error state (handled by Snackbar) or No Exam Data
    // if (error) return <Typography color="error">{error}</Typography>; // Replaced by useErrorHandler Snackbar
    if (!currentExam) return <Typography>Loading exam data...</Typography>; // Or a more specific message
    if (!currentExam.current_module || currentExam.current_module.questions.length === 0) {
        // Handle case where exam loaded but module/questions are missing (e.g., after completion or error)
        if (currentExam.status === 'C') {
             return <Typography>Exam completed. Redirecting...</Typography>; // Or navigate immediately
        }
        return <Typography>No questions available for this section, or the exam is finished.</Typography>;
    }

    // On Break Page
// --- Determine Main Content ---
let mainContent = null; // Variable to hold the content component

if (onBreak) {
    // --- Assign BreakPage to mainContent ---
    mainContent = (
        <BreakPage
            breakendTime={breakendTime} // Pass timestamp directly
            onSkipBreak={handleSkipBreak}
            user={user}
        />
    );
} else if (showReviewPage) {
    // --- Assign ReviewPageComponent to mainContent ---
    mainContent = (
         <ReviewPageComponent
            onClose={closeReviewPage}
            endTime={endTime} // Pass module end time
            handleNextQuestion={() => handleModuleTransition(true)} // Submit Module button
            questions={currentExam.current_module.questions.map((q, index) => ({
                id: q.id,
                text: `Question ${index + 1}`, // Or use q.identifier if available
                answered: isQuestionAnswered(q.id),
                marked: isMarkedReview(q.id), // Pass marked status
            }))}
            currentQuestionIndex={currentQuestionIndex}
            onSelectQuestion={handleSelectQuestion} // Function to jump to a question
            // Pass necessary props down
            currentExam={currentExam}
            user={user}
            isMinimapVisible={isMinimapVisible} // Keep consistent naming if possible
            minimapRef={minimapRef}
            isQuestionAnswered={isQuestionAnswered}
            toggleMinimap={toggleMinimap}
            // handleSelectQuestion={handleSelectQuestion} // Already passed as onSelectQuestion
            openReviewPage={openReviewPage}
            handlePreviousQuestion={handlePreviousQuestion}
            // handleNextQuestion={handleNextQuestion} // Use handleModuleTransition for submit
            showReviewPage={showReviewPage} // Pass state
            order={currentExam?.current_module?.name}
            isMarkedReview={isMarkedReview} // Pass checker function
            handleTimeout={handleTimeout} // Pass timeout handler
        />
    );
} else {
    // --- Assign Main Test UI JSX to mainContent ---
    // Ensure currentQuestion exists before accessing its properties
    const currentQuestion = currentExam.current_module.questions[currentQuestionIndex];
    if (!currentQuestion) {
         // Handle case where question might be missing unexpectedly
         mainContent = <Typography color="error">Error loading current question data.</Typography>;
    } else {
        const isMCQuestion = currentQuestion.question_type === 'MC';
        const userAnswer = selectedAnswers[currentQuestion.id];
        mainContent = (
            <div className="flex-container">
                <TopHeader
                    currentExam={currentExam}
                    endTime={endTime} // Pass module end time
                    handleTimeout={handleTimeout}
                    isMathModule={isMathModule()}
                    toggleCalculator={toggleCalculator}
                    toggleReference={toggleReference}
                    myCalculatorRef={myCalculatorRef} // Pass ref if needed by header
                />

<div className="page-wrapper" id="sandbox">
    {/* Left Panel (Question Text) - MODIFIED */}
    <div className="sidebar-wrapper middle" data-simplebar>
        <div
            // 1. Add 'notranslate' to the existing className
            className="sidebar-content notranslate no-select"
            // 2. Add the translate="no" attribute
            translate="no"
            dangerouslySetInnerHTML={{ __html: processHtmlContent(currentQuestion.text) }}
        />
    </div>

    {/* Right Panel (Question Description/Input) */}
    <div className="page-content-wrapper middle" data-simplebar>
        {/*
           IMPORTANT NOTE FOR RIGHT PANEL:
           Adding translate="no" or className="notranslate" HERE will NOT affect the
           text *inside* the MultipleChoiceQuestion component (like the description or options).
           You MUST modify the MultipleChoiceQuestion component's internal code
           to add these attributes/classes to the specific <Typography> or <FormControlLabel>
           elements (or similar) that render the description and option text within that component.
           Refer back to the previous conceptual example of modifying MultipleChoiceQuestion.js.
        */}
        <MultipleChoiceQuestion
            question={DOMPurify.sanitize(currentQuestion.description)} // Sanitize description
            options={Object.values(currentQuestion.choices || {})} // Use Object.values for array
            selectedValue={userAnswer} // Pass current answer from state
            onValueChange={(eventOrValue) => handleAnswerSelect(currentQuestion.id, eventOrValue)} // Unified handler
            questionNumber={currentQuestionIndex + 1}
            isMCQuestion={isMCQuestion}
            onBlur={!isMCQuestion ? handleInputBlur : undefined} // Blur handler only for non-MC
            markedForReview={markedForReview[currentQuestion.id]}
            onMarkForReview={() => toggleMarkForReview(currentQuestion.id)}
            crossedOut={crossedOut[currentQuestion.id]} // Pass array of booleans
            toggleCrossedOut={(index) => toggleCrossedOut(currentQuestion.id, index)} // Pass index
            toggleTrueCrossedOut={(index) => toggleTrueCrossedOut(currentQuestion.id, index)} // Likely redundant
            crossIcon={crossedIcon} // State for icon toggle
            toggleCrossIcon={() => setCrossedIcon(!crossedIcon)} // Handler for icon toggle
            userInput={userInput} // Pass userInput for fill-in-the-blank display
        />
        {/* Display input validation error */}
        {!isMCQuestion && inputError && (
            <Typography color="error" style={{ marginTop: '10px', fontSize: '0.8rem' }}>
                {inputError}
            </Typography>
        )}
    </div>
</div>
                {/* Draggable Calculator */}
                <CalculatorComponent
             isVisible={calculatorVisible}
     toggleVisibility={() => setCalculatorVisible(!calculatorVisible)}
     position={calculatorPosition}
     onPositionChange={setCalculatorPosition}
     isExpanded={isCalculatorExpanded}
     setIsExpanded={setIsCalculatorExpanded}
     myCalculatorRef={myCalculatorRef}
     toggleCalculator={toggleCalculator}
     isCalculator={true}
   />
        <CalculatorComponent
             isVisible={referenceVisible}
     toggleVisibility={() => setReferenceVisible(!referenceVisible)}
     position={referencePosition}
     
     onPositionChange={setReferencePosition}
    //  isExpanded={isCalculatorExpanded}
    //  setIsExpanded={setIsCalculatorExpanded}
     myCalculatorRef={myCalculatorRef}
     toggleCalculator={toggleReference}
     isCalculator={false}
   />

                {/* Footer */}
                <Footer
                    user={user}
                    isMinimapVisible={isMinimapVisible}
                    minimapRef={minimapRef}
                    currentExam={currentExam}
                    currentQuestionIndex={currentQuestionIndex}
                    isQuestionAnswered={isQuestionAnswered} // Pass checker function
                    toggleMinimap={toggleMinimap}
                    handleSelectQuestion={handleSelectQuestion} // Pass navigation function
                    openReviewPage={openReviewPage}
                    handlePreviousQuestion={handlePreviousQuestion}
                    handleNextQuestion={handleNextQuestion} // Use the correct handler
                    showReviewPage={showReviewPage} // Pass state
                    setMiniMapFalse={setMiniMapFalse} // Function to close minimap
                    isMarkedReview={isMarkedReview} // Pass checker function
                />
            </div>
        );
    }
}

// --- Keep Snackbar Handlers defined before the final return ---
const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
};
const handleCloseErrorSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    clearError(); // Use the clear function from the error handler hook
};

// Keep the render log if needed for debugging
console.error(`!!!!!! (7) TestPage RENDER Cycle !!!!!! errorState.open = ${errorState.open}, Message = "${errorState.message}"`);

// --- Final Return Statement ---
return (
    // Keep FocusLock or <> wrapper
    <FocusLock disabled={!isPageVisible}>
        {/* Info Snackbar */}
        <Snackbar
            open={snackbarOpen}

            autoHideDuration={4000}
            onClose={handleCloseSnackbar} 
             key="snackbar-force-test-high-z"
            sx={{ zIndex: 9999 }} 

        >
            <MuiAlert onClose={handleCloseSnackbar} severity="info" sx={{ width: '100%' }}>
                {snackbarMessage}

            </MuiAlert>
        </Snackbar>

        

        <Snackbar
            open={errorState.open}
            autoHideDuration={errorState.duration}
            onClose={handleCloseErrorSnackbar}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
            <MuiAlert
                elevation={6}
                variant="filled"
                onClose={handleCloseErrorSnackbar}
                severity={errorState.severity}
                sx={{ width: '100%' }}
            >
                {errorState.message}
            </MuiAlert>
        </Snackbar>
         {/* Remove the simple test div if it was present */}
         {/* {errorState.open && ( <div style={{...}}>...</div> )} */}

        {/* Loading Overlay */}
        {loading && <LoadingOverlay text={isTransitioning ? "Transitioning..." : "Loading..."} />}

        {/* Render the determined main content HERE */}
        {mainContent}

    </FocusLock>
);
};

export default TestPage;
